import React, { useState, useEffect } from 'react';
import { Loader2, CheckCircle, AlertCircle, X, Edit3, Trash2 } from 'lucide-react';

/**
 * NotesPanel — gedeeld notitie-tabblad (toevoegen / bewerken / verwijderen)
 * voor de hele pipeline: lead, prospect, onboarding en client. Alle notities
 * hangen aan dezelfde lead-rij via /api/v1/leads/{id}/notes.
 */

const getToken = () => sessionStorage.getItem('auth_token');

async function api(url, options = {}) {
  const resp = await fetch(url, {
    ...options,
    headers: {
      Authorization: `Bearer ${getToken()}`,
      ...(options.body ? { 'Content-Type': 'application/json' } : {}),
      ...options.headers,
    },
  });
  if (!resp.ok) {
    const data = await resp.json().catch(() => ({}));
    throw new Error(data.detail || `API error ${resp.status}`);
  }
  return resp.json();
}

export default function NotesPanel({ leadId, className = '' }) {
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [notesLoading, setNotesLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingNoteText, setEditingNoteText] = useState('');
  const [msg, setMsg] = useState(null);

  const showMsg = (type, text) => {
    setMsg({ type, text });
    setTimeout(() => setMsg(null), 3000);
  };

  const fetchNotes = async () => {
    setNotesLoading(true);
    try {
      const data = await api(`/api/v1/leads/${leadId}`);
      setNotes((data.notes || []).map(n => ({
        id: n.id,
        text: n.content || n.text,
        date: new Date(n.created_at),
        user: n.user_name || 'Gebruiker',
      })));
    } catch {
      setNotes([]);
    }
    setNotesLoading(false);
  };

  useEffect(() => {
    if (leadId) fetchNotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId]);

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setAdding(true);
    try {
      const saved = await api(`/api/v1/leads/${leadId}/notes`, {
        method: 'POST',
        body: JSON.stringify({ content: newNote }),
      });
      setNotes(prev => [{ id: saved?.id || Date.now(), text: newNote, date: new Date(), user: 'Jij' }, ...prev]);
      setNewNote('');
      showMsg('success', 'Notitie toegevoegd');
    } catch (err) {
      showMsg('error', 'Opslaan mislukt: ' + err.message);
    }
    setAdding(false);
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Notitie verwijderen?')) return;
    try {
      await api(`/api/v1/leads/${leadId}/notes/${noteId}`, { method: 'DELETE' });
      setNotes(prev => prev.filter(n => n.id !== noteId));
      showMsg('success', 'Notitie verwijderd');
    } catch (err) { showMsg('error', 'Fout: ' + err.message); }
  };

  const handleEditNote = async (noteId) => {
    if (!editingNoteText.trim()) return;
    try {
      await api(`/api/v1/leads/${leadId}/notes/${noteId}`, {
        method: 'PUT',
        body: JSON.stringify({ content: editingNoteText }),
      });
      setNotes(prev => prev.map(n => n.id === noteId ? { ...n, text: editingNoteText } : n));
      setEditingNoteId(null);
      showMsg('success', 'Notitie bijgewerkt');
    } catch (err) { showMsg('error', 'Fout: ' + err.message); }
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {msg && (
        <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium ${
          msg.type === 'error' ? 'bg-red-50 text-red-600' : 'bg-[#f0fdf4] text-[#16a34a]'
        }`}>
          {msg.type === 'error' ? <AlertCircle size={13} /> : <CheckCircle size={13} />}
          {msg.text}
        </div>
      )}

      {/* Add Note */}
      <div className="space-y-3">
        <textarea
          value={newNote}
          onChange={e => setNewNote(e.target.value)}
          placeholder="Schrijf een notitie..."
          className="w-full px-4 py-3 bg-[#f7f8fc] rounded-lg border border-[#e8eaf2] focus:border-[#3d61a4] focus:outline-none focus:ring-2 focus:ring-[#eef2fa] text-[#566079] placeholder-[#a4abbe] resize-none h-24"
        />
        <button
          onClick={handleAddNote}
          disabled={!newNote.trim() || adding}
          className="px-4 py-2 bg-[#3d61a4] hover:bg-[#0a2d6b] text-white rounded-lg font-medium text-sm transition-colors disabled:opacity-50"
        >
          {adding ? 'Opslaan...' : 'Notitie Toevoegen'}
        </button>
      </div>

      {/* Notes list */}
      {notesLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 size={24} className="animate-spin text-[#3d61a4]" />
        </div>
      ) : notes.length > 0 ? (
        <div className="space-y-3">
          {notes.map(note => (
            <div key={note.id} className="bg-[#f7f8fc] rounded-lg p-4 border border-[#e8eaf2]">
              <div className="flex items-start justify-between gap-2">
                {editingNoteId === note.id ? (
                  <textarea
                    value={editingNoteText}
                    onChange={e => setEditingNoteText(e.target.value)}
                    className="flex-1 text-sm p-2 rounded-lg border border-[#e8eaf2] focus:outline-none focus:border-[#3d61a4] resize-none"
                    rows={3}
                  />
                ) : (
                  <p className="flex-1 text-sm" style={{ color: '#566079', whiteSpace: 'pre-wrap' }}>{note.text}</p>
                )}
                <div className="flex gap-1 flex-shrink-0">
                  {editingNoteId === note.id ? (
                    <>
                      <button onClick={() => handleEditNote(note.id)}
                        className="p-1.5 rounded-lg hover:bg-green-50 transition-colors"
                        style={{ color: '#16a34a' }}>
                        <CheckCircle size={14} />
                      </button>
                      <button onClick={() => setEditingNoteId(null)}
                        className="p-1.5 rounded-lg hover:bg-[#f7f8fc] transition-colors"
                        style={{ color: '#a4abbe' }}>
                        <X size={14} />
                      </button>
                    </>
                  ) : (
                    <>
                      <button onClick={() => { setEditingNoteId(note.id); setEditingNoteText(note.text); }}
                        className="p-1.5 rounded-lg hover:bg-[#eef2fa] transition-colors"
                        style={{ color: '#3d61a4' }}>
                        <Edit3 size={13} />
                      </button>
                      <button onClick={() => handleDeleteNote(note.id)}
                        className="p-1.5 rounded-lg hover:bg-red-50 transition-colors text-[#cdd1e0] hover:text-red-400">
                        <Trash2 size={13} />
                      </button>
                    </>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 mt-2">
                <p className="text-xs text-[#a4abbe]">
                  {note.date instanceof Date ? note.date.toLocaleDateString('nl-NL') : note.date}
                </p>
                {note.user && <span className="text-xs text-[#7b859e]">• {note.user}</span>}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-[#7b859e] text-sm">Nog geen notities</p>
      )}
    </div>
  );
}
